import React, { useEffect, useRef, useState } from 'react';
import { FiChevronDown, FiChevronUp, FiPlus, FiSliders, FiX } from 'react-icons/fi';
import { getColumnLabel } from './PricingColumnCell';

export const SOURCE_OPTIONS = [
    { value: 'all', label: 'All (quotable)' },
    { value: 'estimation', label: 'Estimation only' },
    { value: 'department', label: 'Designer only' },
    { value: 'final', label: 'Final only' },
];

export const GROUPING_OPTIONS = [
    { value: 'total', label: 'Grand total' },
    { value: 'per-department', label: 'Per department' },
    { value: 'per-record', label: 'Per record' },
];

const MAX_COLUMNS = 5;

const columnKey = (column) => `${column.source}-${column.grouping}`;

const columnsEqual = (a, b) =>
    a.length === b.length && a.every((column, index) => columnKey(column) === columnKey(b[index]));

const VALID_SOURCES = new Set(SOURCE_OPTIONS.map(option => option.value));
const VALID_GROUPINGS = new Set(GROUPING_OPTIONS.map(option => option.value));

/**
 * Validates a value loaded from localStorage is a well-formed column-set
 * array before it's trusted — a malformed value (future format change,
 * partial/corrupted write, manual tampering) must degrade to the page's
 * default rather than crash render (there's no ErrorBoundary in this app).
 */
export const sanitizeColumns = (value, defaultColumns) => {
    if (!Array.isArray(value) || value.length === 0) return defaultColumns;

    const isValid = value.every(column =>
        column
        && VALID_SOURCES.has(column.source)
        && VALID_GROUPINGS.has(column.grouping)
    );

    return isValid ? value.slice(0, 5) : defaultColumns;
};

/**
 * The "Columns" toolbar button + builder popover shared by FinalPricings.js
 * and Quotation.js. `allowPerDepartment` is false on Quotation's `separate`
 * view (each row already is one department, so that grouping is
 * meaningless there — see resolvePricingColumn's `department` param).
 */
const PricingColumnsPopover = ({ columns, onChange, defaultColumns, allowPerDepartment = true }) => {
    const [open, setOpen] = useState(false);
    const [draftSource, setDraftSource] = useState('all');
    const [draftGrouping, setDraftGrouping] = useState('total');
    const ref = useRef(null);

    useEffect(() => {
        const handler = (event) => {
            if (ref.current && !ref.current.contains(event.target)) setOpen(false);
        };

        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const groupingOptions = allowPerDepartment
        ? GROUPING_OPTIONS
        : GROUPING_OPTIONS.filter(option => option.value !== 'per-department');

    const effectiveDraftGrouping = groupingOptions.some(option => option.value === draftGrouping)
        ? draftGrouping
        : groupingOptions[0].value;

    const removeColumn = (index) => {
        onChange(columns.filter((_, i) => i !== index));
    };

    const moveColumn = (index, direction) => {
        const target = index + direction;
        if (target < 0 || target >= columns.length) return;

        const next = [...columns];
        [next[index], next[target]] = [next[target], next[index]];
        onChange(next);
    };

    const addColumn = () => {
        const candidate = { source: draftSource, grouping: effectiveDraftGrouping };
        if (columns.some(column => columnKey(column) === columnKey(candidate))) return;

        onChange([...columns, candidate]);
    };

    const resetToDefault = () => {
        onChange(defaultColumns);
        setOpen(false);
    };

    const isDefault = columnsEqual(columns, defaultColumns);

    return (
        <div className="relative" ref={ref}>
            <button
                type="button"
                onClick={() => setOpen(value => !value)}
                className={`flex items-center gap-1.5 h-8 px-3 text-xs font-medium border rounded-md transition-all ${!isDefault || open
                    ? 'bg-emerald-50 border-emerald-400 text-emerald-700'
                    : 'bg-white border-gray-200 text-gray-600 hover:border-emerald-400'
                    }`}
            >
                <FiSliders className="w-3 h-3" />
                Columns
                <span className="bg-gray-200 text-gray-600 text-[10px] rounded-full w-4 h-4 flex items-center justify-center font-bold">
                    {columns.length}
                </span>
                <FiChevronDown className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`} />
            </button>

            {open && (
                <div className="absolute top-full left-0 mt-1 w-80 bg-white border border-gray-200 rounded-xl shadow-xl z-50 p-3 space-y-3">
                    <div>
                        <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Pricing columns</p>
                        <div className="space-y-1">
                            {columns.map((column, index) => (
                                <div key={columnKey(column)} className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-gray-50 border border-gray-200 text-xs">
                                    <span className="flex-1 text-gray-700 font-medium">{getColumnLabel(column)}</span>
                                    <button type="button" onClick={() => moveColumn(index, -1)} disabled={index === 0} className="text-gray-400 hover:text-gray-700 disabled:opacity-30">
                                        <FiChevronUp className="w-3.5 h-3.5" />
                                    </button>
                                    <button type="button" onClick={() => moveColumn(index, 1)} disabled={index === columns.length - 1} className="text-gray-400 hover:text-gray-700 disabled:opacity-30">
                                        <FiChevronDown className="w-3.5 h-3.5" />
                                    </button>
                                    <button type="button" onClick={() => removeColumn(index)} className="text-gray-400 hover:text-red-500">
                                        <FiX className="w-3.5 h-3.5" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="pt-2 border-t border-gray-100 space-y-2">
                        <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">Add column</p>
                        <div className="flex items-center gap-1.5">
                            <select
                                value={draftSource}
                                onChange={(event) => setDraftSource(event.target.value)}
                                className="flex-1 h-8 rounded-md border border-gray-200 px-2 text-xs"
                            >
                                {SOURCE_OPTIONS.map(option => (
                                    <option key={option.value} value={option.value}>{option.label}</option>
                                ))}
                            </select>
                            <select
                                value={effectiveDraftGrouping}
                                onChange={(event) => setDraftGrouping(event.target.value)}
                                className="flex-1 h-8 rounded-md border border-gray-200 px-2 text-xs"
                            >
                                {groupingOptions.map(option => (
                                    <option key={option.value} value={option.value}>{option.label}</option>
                                ))}
                            </select>
                            <button
                                type="button"
                                onClick={addColumn}
                                disabled={columns.length >= MAX_COLUMNS}
                                title={columns.length >= MAX_COLUMNS ? `Up to ${MAX_COLUMNS} columns` : undefined}
                                className="h-8 w-8 shrink-0 flex items-center justify-center rounded-md bg-gray-900 text-white disabled:opacity-30"
                            >
                                <FiPlus className="w-3.5 h-3.5" />
                            </button>
                        </div>
                    </div>

                    {!isDefault && (
                        <button
                            type="button"
                            onClick={resetToDefault}
                            className="w-full py-1.5 text-xs text-red-600 hover:bg-red-50 rounded-md border border-red-200 font-medium"
                        >
                            Reset to default
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

export default PricingColumnsPopover;
